/**
 * V3 ↔ V4 scenario action *config* translation, per V4's OpenAPI
 * `*ActionConfig` schemas. A passthrough writes actions V4 stores but cannot
 * execute — `text_message_on_device` addressed to `devices: [-2]`, no `message`.
 *
 * Both directions are registered together per type: a V4-shaped config reaching
 * the V3 editor crashes it (`OSMAction` maps `recipients.devices` unguarded).
 * Every reader also accepts the V3 spelling, which live V4 rows still carry from
 * before this file translated their type.
 *
 * V4-only fields (`email.html`, `voice_message.repeat`, …) are carried over from
 * the stored config; V3-only ones ride along as extra keys V4 ignores.
 */

import ScenarioAction, { scenarioActionTypes, webhookSignatureTypes } from '../../utils/models/ScenarioAction';
import { forAnyObject, forConcerningObject } from '../../utils/models/Scenario';
import { getLocaleInfo } from '../../../i18n';
import { v3ToV4ActionType } from './translations';
import {
    isAnyId,
    isConcerningId,
    referencedV3Ids,
    toIdArray,
    translateIds,
    untranslateIds,
} from './ids';

const v4 = v3Type => v3ToV4ActionType[v3Type];
const V4_WEBHOOK = v4(scenarioActionTypes.webhook);
const V4_SEND_TO_ARC = v4(scenarioActionTypes.sendToARC);
const V4_OSM = v4(scenarioActionTypes.osm);
const V4_RESEND_LAST_OSM = v4(scenarioActionTypes.resendLastOSM);
const V4_SMS = v4(scenarioActionTypes.sms);
const V4_EMAIL = v4(scenarioActionTypes.email);
const V4_VOICE = v4(scenarioActionTypes.voiceMessage);
const V4_MULTI_TENANT = v4(scenarioActionTypes.multiTenant);
const V4_START_TRACKING = v4(scenarioActionTypes.startTracking);
const V4_CHANGE_TEAM = v4(scenarioActionTypes.changeTeam);
const V4_TOGGLE_TIMER = v4(scenarioActionTypes.toggleTimer);
const V4_ASSISTANCE = v4(scenarioActionTypes.assistanceRequest);
const V4_TOGGLE_SCENARIO = v4(scenarioActionTypes.toggleScenario);

const DEFAULT_METHOD = 'POST';
// The form re-installs this row whenever the list is empty; headersToV4 drops it.
const emptyHeaderRow = () => [{ name: '', value: '' }];

// The model re-seeds `whenEmpty` with '[?]' on every construction, so an
// untouched map is noise; a customised one is user input.
const WHEN_EMPTY_DEFAULT = '[?]';
function customisedWhenEmpty(whenEmpty) {
    if (!whenEmpty || typeof whenEmpty !== 'object') return undefined;
    const keys = Object.keys(whenEmpty);
    if (!keys.length) return undefined;
    return keys.some(k => whenEmpty[k] !== WHEN_EMPTY_DEFAULT) ? whenEmpty : undefined;
}

// The app uses i18next's short codes ('en'), V4 stores BCP-47 ('en-GB') — as
// does the language picker. `getLocaleInfo` falls back to English for anything
// it doesn't list, so an unknown code passes through rather than being rewritten.
const localeCode = lang => {
    if (!lang) return undefined;
    const info = getLocaleInfo(lang);
    return info.key === lang || info.lang === lang ? info.lang : lang;
};

const assign = (target, key, value) => {
    if (value !== undefined) target[key] = value;
    return target;
};

// `addAction` seeds `waitForNewLocation: false` everywhere. Where V4 has no
// field for it, an enabled flag is still user intent (it gates the placeholder
// fallback UI) and rides along; the seeded `false` is dropped.
const keptWaitForNewLocation = cfg => (cfg.waitForNewLocation ? true : undefined);

// V4-only fields: keep what the backend holds rather than blanking them.
function carryOver(out, previous, keys) {
    for (const key of keys) {
        if (previous?.[key] !== undefined && out[key] === undefined) {
            out[key] = previous[key];
        }
    }
    return out;
}

// V3 writes effects as strings; '-' (and duration '0') means "device default".
const EFFECT_UNSET = '-';
const effectSet = value =>
    value !== undefined && value !== null && value !== '' && value !== EFFECT_UNSET;

// Lossy: V4 has one `tone` where V3 keeps one per Twig generation (P5 wins) and
// no screen-light field, so callers keep the raw V3 object alongside.
function effectsToV4(forcedEffects) {
    const fe = forcedEffects || {};
    const out = {};
    const tone = effectSet(fe.toneP5) ? fe.toneP5 : effectSet(fe.toneP4) ? fe.toneP4 : undefined;
    if (tone !== undefined) out.tone = String(tone);
    if (effectSet(fe.volume)) out.volume = +fe.volume;
    if (effectSet(fe.vibra)) out.vibration = String(fe.vibra);
    if (fe.clear !== undefined && fe.clear !== null) out.ack_required = +fe.clear > 0;
    if (effectSet(fe.duration) && +fe.duration > 0) out.duration = +fe.duration;
    return out;
}

function effectsToV3(effects) {
    const e = effects || {};
    const out = {};
    if (e.tone !== undefined && e.tone !== null) {
        out.toneP4 = String(e.tone);
        out.toneP5 = String(e.tone);
    }
    if (e.volume !== undefined && e.volume !== null) out.volume = String(e.volume);
    if (e.vibration !== undefined && e.vibration !== null) out.vibra = String(e.vibration);
    if (e.ack_required !== undefined) out.clear = e.ack_required ? '3' : EFFECT_UNSET;
    if (e.duration !== undefined && e.duration !== null) out.duration = String(e.duration);
    return out;
}

const hasEffects = forcedEffects =>
    !!forcedEffects && Object.keys(effectsToV4(forcedEffects)).length > 0;

// V3 keeps "any" (-1) and "concerning device" (-2) in the id array; V4 declares
// them as booleans beside the uuid list.
function devicesToV4(rawDevices, idMaps) {
    const ids = toIdArray(rawDevices);
    const out = { device_ids: translateIds(ids, idMaps?.deviceIdToUuid) };
    if (ids.some(isConcerningId)) out.for_concerning_device = true;
    if (ids.some(isAnyId)) out.all_other_devices = true;
    return out;
}

function devicesToV3(recipients, idMaps) {
    const r = recipients || {};
    return [
        ...(r.for_concerning_device ? [forConcerningObject] : []),
        ...(r.all_other_devices ? [forAnyObject] : []),
        ...untranslateIds(r.device_ids ?? r.devices, idMaps?.deviceUuidToV3Id),
    ];
}

// Unnamed rows — the blank trailing one included — would reach V4 as an
// empty-string header name. Duplicate names collapse into the Record.
function headersToV4(headers) {
    const list = Array.isArray(headers) ? headers : [];
    const out = {};
    for (const header of list) {
        const name = (header?.name || '').trim();
        if (!name) continue;
        out[name] = (header?.value || '').trim();
    }
    return out;
}

function headersToV3(headers) {
    // Already V3-shaped, i.e. stored before this file existed. Copy the rows:
    // the form edits them in place.
    if (Array.isArray(headers)) {
        return headers.length ? headers.map(h => ({ ...h })) : emptyHeaderRow();
    }
    if (!headers || typeof headers !== 'object') return emptyHeaderRow();
    const names = Object.keys(headers);
    if (!names.length) return emptyHeaderRow();
    return names.map(name => ({
        name,
        value: headers[name] == null ? '' : String(headers[name]),
    }));
}

function webhookConfigToV4(config) {
    const cfg = config || {};
    const useCustomBody = !!cfg.useCustomBody;
    const out = {
        url: cfg.url || '',
        // The form forces POST while a custom body is on — send what it shows.
        method: useCustomBody ? DEFAULT_METHOD : cfg.method || DEFAULT_METHOD,
        headers: headersToV4(cfg.headers),
        wait_for_new_location: cfg.waitForNewLocation !== false,
        include_live_location: !!cfg.includeLiveLocation,
        custom_body: { enabled: useCustomBody, template: cfg.text || '' },
        // V4 types this as a free string and its vocabulary is unverified; an
        // unrecognised value fails the save loudly, which beats omitting the
        // field and silently shipping an unsigned webhook.
        signature_type: cfg.signatureType || webhookSignatureTypes.legacy,
    };
    // Only the sha265 scheme carries one — the form deletes it for legacy.
    if (cfg.secret) out.secret = cfg.secret;
    return assign(out, 'whenEmpty', customisedWhenEmpty(cfg.whenEmpty));
}

// Emit what the V3 form would normalise the config to, so opening the action
// doesn't trigger its render-time patch-and-onChange.
function webhookConfigToV3(config) {
    const cfg = config || {};
    const customBody = cfg.custom_body || {};
    const template = customBody.template ?? cfg.text ?? '';
    const secret = cfg.secret;
    return {
        url: cfg.url || '',
        method: cfg.method || DEFAULT_METHOD,
        headers: headersToV3(cfg.headers),
        // A secret with no scheme means sha265; legacy would delete it.
        signatureType:
            cfg.signature_type ||
            cfg.signatureType ||
            (secret ? webhookSignatureTypes.sha265Signature : webhookSignatureTypes.legacy),
        secret,
        waitForNewLocation: (cfg.wait_for_new_location ?? cfg.waitForNewLocation) !== false,
        includeLiveLocation: !!(cfg.include_live_location ?? cfg.includeLiveLocation),
        // A template stored without the flag reads as enabled.
        useCustomBody: !!(customBody.enabled ?? cfg.useCustomBody ?? template),
        text: template,
        ...(cfg.whenEmpty ? { whenEmpty: cfg.whenEmpty } : {}),
    };
}

function osmConfigToV4(config, { idMaps } = {}) {
    const cfg = config || {};
    const text = cfg.text || '';
    const recipients = devicesToV4(cfg.recipients?.devices, idMaps);
    // V4 declares this one camelCase, unlike every other recipients field.
    recipients.excludeOriginator =
        !recipients.for_concerning_device && cfg.recipients?.excludeOriginator !== false;
    // `TextMessageRecipientsConfig` has no team/geofence dimension — kept as
    // extra keys for the editor; V4 delivers only to device_ids.
    assign(recipients, 'teams', pickIfAny(cfg.recipients?.teams));
    assign(recipients, 'geofences', pickIfAny(cfg.recipients?.geofences));

    const out = {
        recipients,
        // The server's own payload carries both, with the same string.
        message: text,
        text,
    };
    assign(out, 'language', localeCode(cfg.language));
    if (hasEffects(cfg.forcedEffects)) {
        out.effects = effectsToV4(cfg.forcedEffects);
        out.forcedEffects = cfg.forcedEffects;
    }
    if (cfg.includeOfflineAppUsers) out.includeOfflineAppUsers = true;
    assign(out, 'followUpScenarioId', cfg.followUpScenarioId || undefined);
    assign(out, 'waitForNewLocation', keptWaitForNewLocation(cfg));
    return assign(out, 'whenEmpty', customisedWhenEmpty(cfg.whenEmpty));
}

function osmConfigToV3(config, { idMaps } = {}) {
    const cfg = config || {};
    const r = cfg.recipients || {};
    const out = {
        // All three must exist: `OSMAction`, `canSave` and
        // `ScenarioActionSummary` index them unguarded.
        recipients: {
            devices: devicesToV3(r, idMaps),
            teams: toIdArray(r.teams),
            geofences: toIdArray(r.geofences),
            excludeOriginator: !r.for_concerning_device && r.excludeOriginator !== false,
        },
        text: cfg.message ?? cfg.text ?? '',
        language: localeCode(cfg.language),
        waitForNewLocation: !!cfg.waitForNewLocation,
    };
    if (cfg.forcedEffects) out.forcedEffects = cfg.forcedEffects;
    else if (cfg.effects) out.forcedEffects = effectsToV3(cfg.effects);
    if (cfg.includeOfflineAppUsers) out.includeOfflineAppUsers = true;
    assign(out, 'followUpScenarioId', cfg.followUpScenarioId);
    return assign(out, 'whenEmpty', cfg.whenEmpty);
}

const pickIfAny = rawIds => {
    const ids = toIdArray(rawIds);
    return ids.length ? ids : undefined;
};

// V4 types this one `OsmActionConfig`, despite the name. The editor's "use
// different forced effects" checkbox is `forcedEffects` being non-null.
function resendLastOsmConfigToV4(config) {
    const cfg = config || {};
    const useDifferent = !!cfg.forcedEffects;
    const out = { use_different_effects: useDifferent };
    if (useDifferent) {
        out.effects = effectsToV4(cfg.forcedEffects);
        out.forcedEffects = cfg.forcedEffects;
    }
    return out;
}

function resendLastOsmConfigToV3(config) {
    const cfg = config || {};
    // Pre-translation configs have no flag — there, `forcedEffects` IS it.
    if (!cfg.use_different_effects && !cfg.forcedEffects) return { forcedEffects: null };
    return { forcedEffects: cfg.forcedEffects || effectsToV3(cfg.effects) };
}

const recipientList = recipients => (Array.isArray(recipients) ? recipients : []);

// V4 takes addresses only, each once (`unique`): a picked contact goes out as
// its address. A contact without one stays a token, which V4 refuses.
function recipientsToV4(recipients, contactAddresses) {
    const out = [];
    recipientList(recipients).forEach(recipient => {
        const id = ScenarioAction.contactRecipientId(recipient);
        const address = (id !== null && contactAddresses?.[id]) || recipient;
        if (!out.includes(address)) out.push(address);
    });
    return out;
}

// V4 drops any contact link, so a contact's address comes back as the contact.
function recipientsToV3(recipients, addressToContactId) {
    return recipientList(recipients).map(recipient => {
        const id = addressToContactId?.[String(recipient).toLowerCase()];
        return id == null ? recipient : `{{c-${id}}}`;
    });
}

function smsConfigToV4(config, { idMaps } = {}) {
    const cfg = config || {};
    const out = {
        recipients: recipientsToV4(cfg.recipients, idMaps?.contactIdToPhone),
        // V4 spells the message `body`.
        body: cfg.text || '',
    };
    assign(out, 'language', localeCode(cfg.language));
    if (cfg.originator) out.originator = cfg.originator;
    if (cfg.considerOOO) out.considerOOO = true;
    assign(out, 'waitForNewLocation', keptWaitForNewLocation(cfg));
    return assign(out, 'whenEmpty', customisedWhenEmpty(cfg.whenEmpty));
}

function smsConfigToV3(config, { idMaps } = {}) {
    const cfg = config || {};
    const out = {
        recipients: recipientsToV3(cfg.recipients, idMaps?.phoneToContactId),
        text: cfg.body ?? cfg.text ?? '',
        language: localeCode(cfg.language),
        waitForNewLocation: !!cfg.waitForNewLocation,
    };
    assign(out, 'originator', cfg.originator);
    if (cfg.considerOOO) out.considerOOO = true;
    return assign(out, 'whenEmpty', cfg.whenEmpty);
}

const EMAIL_V4_ONLY = ['html', 'origin', 'include_google_maps'];

function emailConfigToV4(config, { previous, idMaps } = {}) {
    const cfg = config || {};
    const out = {
        recipients: recipientsToV4(cfg.recipients, idMaps?.contactIdToEmail),
        subject: cfg.subject || '',
        text: cfg.text || '',
        wait_for_new_location: !!cfg.waitForNewLocation,
    };
    assign(out, 'language', localeCode(cfg.language));
    if (cfg.considerOOO) out.considerOOO = true;
    carryOver(out, previous, EMAIL_V4_ONLY);
    return assign(out, 'whenEmpty', customisedWhenEmpty(cfg.whenEmpty));
}

function emailConfigToV3(config, { idMaps } = {}) {
    const cfg = config || {};
    const out = {
        recipients: recipientsToV3(cfg.recipients, idMaps?.emailToContactId),
        // `undefined` re-arms the editor's subject-defaulting setTimeout.
        subject: cfg.subject ?? '',
        text: cfg.text || '',
        language: localeCode(cfg.language),
        waitForNewLocation: !!(cfg.wait_for_new_location ?? cfg.waitForNewLocation),
    };
    if (cfg.considerOOO) out.considerOOO = true;
    return assign(out, 'whenEmpty', cfg.whenEmpty);
}

function voiceConfigToV4(config, { previous, idMaps } = {}) {
    const cfg = config || {};
    const out = {
        recipients: recipientsToV4(cfg.recipients, idMaps?.contactIdToPhone),
        text: cfg.text || '',
    };
    assign(out, 'language', localeCode(cfg.language));
    if (cfg.originator) out.originator = cfg.originator;
    if (cfg.considerOOO) out.considerOOO = true;
    carryOver(out, previous, ['repeat']);
    assign(out, 'waitForNewLocation', keptWaitForNewLocation(cfg));
    return assign(out, 'whenEmpty', customisedWhenEmpty(cfg.whenEmpty));
}

function voiceConfigToV3(config, { idMaps } = {}) {
    const cfg = config || {};
    const out = {
        recipients: recipientsToV3(cfg.recipients, idMaps?.phoneToContactId),
        text: cfg.text || '',
        // `canSave` fails the whole scenario without one.
        language: localeCode(cfg.language) || localeCode('en'),
        waitForNewLocation: !!cfg.waitForNewLocation,
    };
    assign(out, 'originator', cfg.originator);
    if (cfg.considerOOO) out.considerOOO = true;
    return assign(out, 'whenEmpty', cfg.whenEmpty);
}

function multiTenantConfigToV4(config, { previous } = {}) {
    const cfg = config || {};
    const out = { text: cfg.text || '' };
    // Never regenerate: the code pairs this action with another tenant's
    // trigger, and the editor refetches one the moment it reads none.
    assign(out, 'code', cfg.code);
    carryOver(out, previous, ['language']);
    assign(out, 'waitForNewLocation', keptWaitForNewLocation(cfg));
    return assign(out, 'whenEmpty', customisedWhenEmpty(cfg.whenEmpty));
}

function multiTenantConfigToV3(config) {
    const cfg = config || {};
    const out = { text: cfg.text || '', waitForNewLocation: !!cfg.waitForNewLocation };
    assign(out, 'code', cfg.code);
    assign(out, 'language', cfg.language);
    return assign(out, 'whenEmpty', cfg.whenEmpty);
}

// Seconds on both sides; the editor only displays minutes.
function trackingConfigToV4(config) {
    const cfg = config || {};
    const out = {};
    if (+cfg.interval > 0) out.interval = +cfg.interval;
    if (+cfg.duration > 0) out.duration = +cfg.duration;
    return out;
}

function trackingConfigToV3(config) {
    const cfg = config || {};
    const out = {};
    // Absent rather than 0: the editor re-seeds 60/1800, and `canSave` reads
    // a 0 as "not configured".
    if (+cfg.interval > 0) out.interval = +cfg.interval;
    if (+cfg.duration > 0) out.duration = +cfg.duration;
    return out;
}

// V3 stores '1' (start) / '0' (stop); V4 names it.
function toggleTimerConfigToV4(config) {
    const mode = (config || {}).mode;
    return { action: String(mode) === '0' || mode === 0 || mode === false ? 'stop' : 'start' };
}

function toggleTimerConfigToV3(config) {
    // Reading `action` alone inverts a pre-translation config: the editor sees
    // no `mode`, patches it to '1', and "stop timer" silently becomes "start".
    const cfg = config || {};
    const stop = cfg.action ? cfg.action === 'stop' : String(cfg.mode) === '0';
    return { mode: stop ? '0' : '1' };
}

const changeTeamAction = action => (action === 'join' ? 'join' : 'leave');

// V3 keeps a team list per action; V4 takes one `team_id`, so one V3 action
// fans out per team and `mapV4ScenarioToV3` folds them back.
function changeTeamConfigsToV4(config, { idMaps } = {}) {
    const cfg = config || {};
    const action = changeTeamAction(cfg.action);
    const rawIds = toIdArray(cfg.teamIds);
    if (rawIds.some(isAnyId)) {
        throw new Error(
            'V4 has no "all teams" option for a change-team action — select the teams explicitly.'
        );
    }
    const teamIds = translateIds(rawIds, idMaps?.teamIdToUuid);
    if (!teamIds.length) {
        throw new Error(
            'the change-team action references no team that exists in V4 — select a team that has been migrated.'
        );
    }
    return teamIds.map(teamId => ({ team_id: teamId, action }));
}

function changeTeamConfigToV3(config, { idMaps } = {}) {
    const cfg = config || {};
    // A pre-translation config holds `teamIds` and no `team_id`; reading only
    // the V4 key empties the picker, then throws above on every save.
    const stored = cfg.team_id ? [cfg.team_id] : toIdArray(cfg.teamIds);
    return {
        teamIds: untranslateIds(stored, idMaps?.teamUuidToV3Id),
        action: changeTeamAction(cfg.action),
    };
}

function assistanceConfigToV4(config, { idMaps } = {}) {
    const cfg = config || {};
    const r = cfg.recipients || {};
    const deviceIds = toIdArray(r.devices);
    const teamIds = toIdArray(r.teams);
    const geofenceIds = toIdArray(r.geofences);

    const recipients = {
        device_ids: translateIds(deviceIds, idMaps?.deviceIdToUuid),
        team_ids: translateIds(teamIds, idMaps?.teamIdToUuid),
        geofence_ids: translateIds(geofenceIds, idMaps?.geofenceIdToUuid),
    };
    if (deviceIds.some(isConcerningId)) recipients.for_concerning_device = true;
    if (deviceIds.some(isAnyId)) recipients.all_other_devices = true;
    if (teamIds.some(isAnyId)) recipients.all_connected_teams = true;
    if (geofenceIds.some(isAnyId)) recipients.all_geofences_where_present = true;
    if (r.excludeOriginator === false || recipients.for_concerning_device) {
        recipients.excludeOriginator = false;
    }

    const out = {
        name: cfg.name || '',
        recipients,
        message: cfg.text || '',
        wait_for_new_location: !!cfg.waitForNewLocation,
    };
    assign(out, 'language', localeCode(cfg.language));
    if (+cfg.delay > 0) out.delay = +cfg.delay;
    if (+cfg.accepts > 0) out.min_turnout = +cfg.accepts;
    const followUp = translateIds(
        cfg.followUpScenarioId ? [cfg.followUpScenarioId] : [],
        idMaps?.scenarioIdToUuid
    );
    if (followUp.length) out.follow_up_scenario_id = followUp[0];
    if (cfg.includeOfflineAppUsers) out.includeOfflineAppUsers = true;
    return assign(out, 'whenEmpty', customisedWhenEmpty(cfg.whenEmpty));
}

function assistanceConfigToV3(config, { idMaps } = {}) {
    const cfg = config || {};
    const r = cfg.recipients || {};
    const storedTurnout = cfg.min_turnout ?? cfg.accepts;
    const out = {
        name: cfg.name || '',
        recipients: {
            devices: devicesToV3(r, idMaps),
            teams: [
                ...(r.all_connected_teams ? [forAnyObject] : []),
                ...untranslateIds(r.team_ids ?? r.teams, idMaps?.teamUuidToV3Id),
            ],
            geofences: [
                ...(r.all_geofences_where_present ? [forAnyObject] : []),
                ...untranslateIds(r.geofence_ids ?? r.geofences, idMaps?.geofenceUuidToV3Id),
            ],
            excludeOriginator: !r.for_concerning_device && r.excludeOriginator !== false,
        },
        text: cfg.message ?? cfg.text ?? '',
        language: localeCode(cfg.language),
        waitForNewLocation: !!(cfg.wait_for_new_location ?? cfg.waitForNewLocation),
        // The editor patches-and-reloads when either is absent or NaN. The V3
        // spellings are read too, or a 3-responder request drops back to 1.
        delay: +cfg.delay > 0 ? +cfg.delay : 30,
        accepts: +storedTurnout > 0 ? +storedTurnout : 1,
    };
    const storedFollowUp = cfg.follow_up_scenario_id ?? cfg.followUpScenarioId;
    const followUp = untranslateIds(
        storedFollowUp ? [storedFollowUp] : [],
        idMaps?.scenarioUuidToV3Id
    );
    if (followUp.length) out.followUpScenarioId = followUp[0];
    if (cfg.includeOfflineAppUsers) out.includeOfflineAppUsers = true;
    return assign(out, 'whenEmpty', cfg.whenEmpty);
}

// The picker lists V4 scenarios, so the ids are uuids already.
function toggleScenarioConfigToV4(config) {
    const cfg = config || {};
    const scenarioIds = Array.from(new Set(translateIds(cfg.scenarioIds)));
    if (!scenarioIds.length) {
        throw new Error(
            'the enable/disable scenarios action selects no V4 scenario — select at least one scenario.'
        );
    }
    return { action: cfg.action, scenario_ids: scenarioIds };
}

function toggleScenarioConfigToV3(config) {
    const cfg = config || {};
    return {
        action: cfg.action,
        scenarioIds: [...toIdArray(cfg.scenario_ids ?? cfg.scenarioIds)],
    };
}

// V4 declares `send_to_arc` configless; drop the two keys the V3 model seeds.
function sendToArcConfigToV4(config) {
    const { whenEmpty, waitForNewLocation, ...rest } = config || {};
    return rest;
}

const translators = {
    [V4_WEBHOOK]: { toV4: webhookConfigToV4, toV3: webhookConfigToV3 },
    [V4_SEND_TO_ARC]: { toV4: sendToArcConfigToV4 },
    [V4_OSM]: { toV4: osmConfigToV4, toV3: osmConfigToV3 },
    [V4_RESEND_LAST_OSM]: { toV4: resendLastOsmConfigToV4, toV3: resendLastOsmConfigToV3 },
    [V4_SMS]: { toV4: smsConfigToV4, toV3: smsConfigToV3 },
    [V4_EMAIL]: { toV4: emailConfigToV4, toV3: emailConfigToV3 },
    [V4_VOICE]: { toV4: voiceConfigToV4, toV3: voiceConfigToV3 },
    [V4_MULTI_TENANT]: { toV4: multiTenantConfigToV4, toV3: multiTenantConfigToV3 },
    [V4_START_TRACKING]: { toV4: trackingConfigToV4, toV3: trackingConfigToV3 },
    [V4_TOGGLE_TIMER]: { toV4: toggleTimerConfigToV4, toV3: toggleTimerConfigToV3 },
    [V4_CHANGE_TEAM]: { toV4: changeTeamConfigsToV4, toV3: changeTeamConfigToV3 },
    [V4_ASSISTANCE]: { toV4: assistanceConfigToV4, toV3: assistanceConfigToV3 },
    [V4_TOGGLE_SCENARIO]: { toV4: toggleScenarioConfigToV4, toV3: toggleScenarioConfigToV3 },
};

/** True when one V3 action of this type can produce several V4 actions. */
export const fansOutInV4 = v4Type => v4Type === V4_CHANGE_TEAM;

// Recipients each translator sends by uuid; OSM's teams and geofences are extra
// keys V4 ignores.
const uuidRecipientKinds = {
    [V4_OSM]: ['devices'],
    [V4_ASSISTANCE]: ['devices', 'teams', 'geofences'],
};

/** V3 recipient ids in `config` with no uuid in `idMaps`, per entity type. */
export function unresolvedRecipientIds(v4Type, config, idMaps = {}) {
    const recipients = config?.recipients || {};
    const maps = {
        devices: idMaps.deviceIdToUuid,
        teams: idMaps.teamIdToUuid,
        geofences: idMaps.geofenceIdToUuid,
    };
    const out = { devices: [], teams: [], geofences: [] };
    for (const kind of uuidRecipientKinds[v4Type] || []) {
        out[kind] = referencedV3Ids(recipients[kind]).filter(id => !maps[kind]?.[id]);
    }
    return out;
}

/**
 * V3 editor config → V4 action configs (an array: `change_team` fans out).
 * `ctx.idMaps` resolves V3 ids to uuids; `ctx.previous` is the stored V4 config.
 */
export function toV4ActionConfigs(v4Type, config, ctx = {}) {
    const toV4 = translators[v4Type]?.toV4;
    // An unmapped type echoes what V4 stored, minus the seeded fallbacks.
    if (!toV4) {
        const { whenEmpty, ...rest } = config || {};
        return [assign(rest, 'whenEmpty', customisedWhenEmpty(whenEmpty))];
    }
    const result = toV4(config, ctx);
    return Array.isArray(result) ? result : [result];
}

/** The single V4 config for a type that does not fan out. */
export function toV4ActionConfig(v4Type, config, ctx = {}) {
    return toV4ActionConfigs(v4Type, config, ctx)[0];
}

/** V4 config → the shape the V3 editor reads. `v4Type` is the raw V4 type. */
export function toV3ActionConfig(v4Type, config, ctx = {}) {
    const toV3 = translators[v4Type]?.toV3;
    if (!toV3) return config && typeof config === 'object' ? config : {};
    return toV3(config, ctx);
}
